import React, { useState, useEffect } from "react";
import axios from "axios";
import { FetchMedia } from "../../../hooks/APIHooks";

const MediaManager = () => {
  const [mediaFiles, setMediaFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    loadMedia();
  }, []);

  const loadMedia = async () => {
    try {
      setLoading(true);
      const res = await axios.get(FetchMedia);
      if (res.data.success) {
        setMediaFiles(res.data.files || []);
      } else {
        setMessage({ type: "error", text: res.data.message });
      }
    } catch (e) {
      console.error("Load error:", e);
      setMessage({ type: "error", text: "Failed to load media files" });
    } finally {
      setLoading(false);
    }
  };

  const getMediaUrl = (path) => {
    if (!path) return "";
    if (path.startsWith('http')) return path;
    
    const cleanPath = path.replace(/^(\.?\/?)?/, '');
    
    // Handle different environments like your Settings component
    let baseUrl;
    const { hostname, port, protocol } = window.location;
    
    if (hostname === 'localhost' && port === '3000') {
      baseUrl = `${protocol}//${hostname}`;
    } else {
      baseUrl = window.location.origin;
    }
    
    return `${baseUrl}/apis/${cleanPath}`;
  };

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;

    const valid = ["image/jpeg", "image/png", "image/webp", "image/jpg", "image/gif"];
    if (!valid.includes(f.type)) {
      setMessage({ type: "error", text: "Invalid file format. Please select JPG, PNG, GIF, or WebP." });
      e.target.value = '';
      return;
    }

    if (f.size > 5 * 1024 * 1024) {
      setMessage({ type: "error", text: "File size too large. Maximum 5MB allowed." });
      e.target.value = '';
      return;
    }

    setSelectedFile(f);
    setMessage({ type: "", text: "" });
  };

  const uploadFile = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      setMessage({ type: "error", text: "Please select a file to upload" });
      return;
    }

    setLoading(true);
    setMessage({ type: "", text: "" });
    setUploadProgress(0);

    const fd = new FormData();
    fd.append("media_file", selectedFile);

    try {
      const res = await axios.post(FetchMedia, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (ev) => {
          if (ev.total) {
            setUploadProgress(Math.round((ev.loaded / ev.total) * 100));
          }
        },
      });

      if (res.data.success) {
        setMessage({ type: "success", text: "File uploaded successfully" });
        setSelectedFile(null);
        setUploadProgress(0);
        document.getElementById('file-input').value = '';
        loadMedia();
      } else {
        setMessage({ type: "error", text: res.data.message });
      }
    } catch (err) {
      console.error("Upload error:", err);
      setMessage({ 
        type: "error", 
        text: err.response?.data?.message || "Upload failed. Please try again." 
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteFile = async (fileName) => {
    if (!window.confirm("Are you sure you want to delete this file?")) return;

    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const res = await axios.delete(FetchMedia, { 
        data: { file_name: fileName } 
      });
      
      if (res.data.success) {
        setMessage({ type: "success", text: "File deleted successfully" });
        loadMedia();
      } else {
        setMessage({ type: "error", text: res.data.message });
      }
    } catch (err) {
      console.error("Delete error:", err);
      setMessage({ 
        type: "error", 
        text: err.response?.data?.message || "Delete failed. Please try again." 
      });
    } finally {
      setLoading(false);
    }
  };

  const copyURL = (path) => {
    const fullUrl = getMediaUrl(path);
    navigator.clipboard.writeText(fullUrl);
    setMessage({ type: "success", text: "URL copied to clipboard!" });
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">Media Manager</h1>

      {/* Upload Section */}
      <div className="bg-white p-6 shadow rounded-lg mb-6">
        <h2 className="text-xl font-semibold mb-4">Upload New Image</h2>
        <form onSubmit={uploadFile} className="space-y-4">
          <div>
            <input 
              id="file-input"
              type="file" 
              accept="image/*" 
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              required
            />
            <p className="text-xs text-gray-500 mt-1">Supported formats: JPG, PNG, GIF, WebP. Max size: 5MB</p>
          </div>
          
          {selectedFile && (
            <p className="text-sm text-gray-600">Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)</p>
          )}

          {uploadProgress > 0 && uploadProgress < 100 && (
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div 
                className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" 
                style={{ width: `${uploadProgress}%` }}
              ></div>
              <div className="text-xs text-gray-600 mt-1 text-center">{uploadProgress}%</div>
            </div>
          )}

          <button 
            disabled={!selectedFile || loading}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? "Uploading..." : "Upload Image"}
          </button>
        </form>
      </div>

      {/* Messages */}
      {message.text && (
        <div className={`p-4 mb-6 rounded-lg ${
          message.type === "error" 
            ? "bg-red-100 text-red-700 border border-red-200" 
            : "bg-green-100 text-green-700 border border-green-200"
        }`}>
          {message.text}
        </div>
      )}

      {/* Gallery */}
      <div className="bg-white p-6 shadow rounded-lg">
        <h2 className="text-xl font-semibold mb-4">
          Media Library ({mediaFiles.length} files)
        </h2>
        
        {loading && mediaFiles.length === 0 ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
            <p className="text-gray-600 mt-2">Loading media files...</p>
          </div>
        ) : mediaFiles.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {mediaFiles.map((file) => (
              <div key={file.file_name} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
                <div className="aspect-square bg-gray-100 flex items-center justify-center">
                  <img
                    src={getMediaUrl(file.file_path)}
                    alt={file.file_name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2YzZjRmNiIvPjx0ZXh0IHg9IjEwMCIgeT0iMTAwIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTQiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiIGZpbGw9IiM5YTliYWIiPkltYWdlIE5vdCBGb3VuZDwvdGV4dD48L3N2Zz4=';
                    }}
                  />
                </div>
                <div className="p-3">
                  <p className="text-sm font-medium text-gray-800 truncate" title={file.file_name}>
                    {file.file_name}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(file.upload_date).toLocaleDateString()}
                  </p>
                  <div className="flex space-x-2 mt-3">
                    <button 
                      onClick={() => copyURL(file.file_path)}
                      className="flex-1 bg-gray-100 text-gray-700 px-2 py-1 text-xs rounded hover:bg-gray-200 transition-colors"
                    >
                      Copy URL
                    </button>
                    <button 
                      onClick={() => deleteFile(file.file_name)}
                      className="flex-1 bg-red-100 text-red-700 px-2 py-1 text-xs rounded hover:bg-red-200 transition-colors disabled:opacity-50"
                      disabled={loading}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <p>No media files found. Upload your first image above.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MediaManager;